import React, { useState, useEffect, useRef } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import { useTranslation } from 'react-i18next';
import Schema from "./Components/Schema";
import Select from "react-select";
import Button from "../Tools/Components/Button";

const CreateSchemaSetPagina = () => {

	// these values should never change after the init method
	const [groupName, setGroupName] = useState("");
	const [setName, setSetName] = useState("");

	// default values consciously set up to allow a return with minimal extra logic in the init useEffect in the case of a fully new set
	const [selectedSetVersion, setSelectedSetVersion] = useState("production");
	const [versions, setVersions] = useState([]);
	const [selectedSchema, setSelectedSchema] = useState({});
	const [newSet, setNewSet] = useState(true);
	const [editingFiles, setEditingFiles] = useState(true);
	const [editingVersion, setEditingVersion] = useState(false);
	const [inputSetName, setInputSetName] = useState("");
	const [tempVersionName, setTempVersionName] = useState("");
	const [tempSchemaSetVer, setTempSchemaSetVer] = useState({
		name: "",
		dependencyGroupName: "",
		version: "",
		topSchemas: []
	});

	const { t } = useTranslation();

	const waitingOnFileRead = useRef(false);
	const axiosRef = useRef(null);

	const handleVersionEdit = () => {
		const axios = axiosRef.current;
		setEditingVersion(false);
		if (editingFiles) {
			const tempSchemaSetCopy = structuredClone(tempSchemaSetVer);
			tempSchemaSetCopy.version = tempVersionName;
			setTempSchemaSetVer(tempSchemaSetCopy);
			return;
		}

		axios.put(process.env.REACT_APP_SD_VALIDATOR_URL + "SchemaSet/" + groupName + '/' + setName + "?currentVersion=" + encodeURIComponent(selectedSetVersion) + "&newVersion=" + encodeURIComponent(tempVersionName))
			.then(response => {
				if (response.status !== 200) throw new Error("updating version failed, reason: " + response.data);
				window.location.reload();
			})
			.catch(ex => {
				alert(ex.response?.data ?? ex.message);
				window.location.reload();
			});
	};

	const handleStartSetCreation = (groupNameOverride = groupName) => {
		setEditingFiles(true);

		const tempSchemaSetCopy = structuredClone(tempSchemaSetVer);
		tempSchemaSetCopy.dependencyGroupName = groupNameOverride;
		if (newSet) {
			tempSchemaSetCopy.version = "production";
		}
		else {
			setEditingVersion(true);
			setSelectedSetVersion("");
			tempSchemaSetCopy.name = setName;
			tempSchemaSetCopy.topSchemas = (versions.find(ver => ver.version === selectedSetVersion))?.topSchemas ?? [];
		}
		setTempSchemaSetVer(tempSchemaSetCopy);
	};

	const handleSetVersionCreation = () => {
		if (waitingOnFileRead.current) {
			alert("waiting on file read, please wait briefly, and then try again");
			return;
		}
		const tempSchemaSetCopy = structuredClone(tempSchemaSetVer);

		if (newSet) {
			tempSchemaSetCopy.name = inputSetName;
		}
		else { tempSchemaSetCopy.version = tempVersionName; }
		console.log(groupName);
		console.log(tempSchemaSetCopy);
		const axios = axiosRef.current;

		axios.post(process.env.REACT_APP_SD_VALIDATOR_URL + "SchemaSet", tempSchemaSetCopy)
			.then(response => {
				if (response.status !== 200) throw new Error(response.data);
				if (newSet) {
					window.location.assign("CreateValidatorSet/" + groupName + "/" + response.data);
					return;
				}
				window.location.reload();
			})
			.catch(ex => {
				alert(ex.response?.data ?? ex.message);
				window.location.reload();
			});
	};

	const handleCreateSchemaFromFile = (file) => {
		const reader = new FileReader();
		var version;
		if (file.name.toLowerCase().endsWith(".sch")) version = 2;
		else if (file.name.toLowerCase().endsWith(".xslt") || file.name.toLowerCase().endsWith(".xsl")) version = 1;
		else version = 0;

		reader.onload = (e) => {
			const text = e.target.result;
			const tempSchemaSetCopy = structuredClone(tempSchemaSetVer);
			tempSchemaSetCopy.topSchemas[tempSchemaSetCopy.topSchemas.length] = {
				name: file.name.split('.')[0],
				value: text,
				valueLoaded: true,
				schemaType: version,
				dependencies: []
			};
			setTempSchemaSetVer(tempSchemaSetCopy);
			waitingOnFileRead.current = false;
		};

		reader.onerror = (e) => {
			alert('Error reading file:', e.target.error);
			waitingOnFileRead.current = false;
		};
		waitingOnFileRead.current = true;
		reader.readAsText(file);
	};

	useEffect(() => {
		const httpService = HttpService;
		httpService.configure();
		axiosRef.current = httpService.getAxiosClient();

		const axios = axiosRef.current;
		if (!axios) return;
		const urlParts = window.location.href.split('/');
		// if creating a new set it is expected that the groupname is the last arg
		if (urlParts[urlParts.length - 2].toLowerCase() === "createvalidatorset") {
			const groupName = urlParts[urlParts.length - 1];
			setGroupName(groupName);
			const setTemplate = {
				name: "",
				dependencyGroupName: groupName,
				version: "production",
				topSchemas: []
			}
			setVersions([setTemplate]);
			setSelectedSchema(setTemplate);
			handleStartSetCreation(groupName);
			return;
		}
		// else it is assumed that a existing set is being viewed, which means the second last arg is the group name, and the last arg is the set name
		const groupName = urlParts[urlParts.length - 2];
		const setName = urlParts[urlParts.length - 1];

		setGroupName(groupName);
		setSetName(setName);

		setNewSet(false);
		setEditingFiles(false);

		axios.get(process.env.REACT_APP_SD_VALIDATOR_URL + "SchemaSet/all/" + groupName + '/' + setName + "?loadValues=true")
			.then(response => {
				if (response.data.length <= 0) alert("could not load set values, please contact a developer");
				setVersions(response.data);
				setSelectedSchema(response.data[0].topSchemas[0]);
			})
			.catch(ex => alert(ex.response?.data ?? ex.message));
			// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);


	return (
		<div className="relative size-full">
			<div className="flex flex-row size-full gap-5">
				{/* MAIN CONTENT */}
				<div className="flex flex-col items-center size-full py-2">

					{/* HEADER */}
					<div className="bg-[#00aaad] dark:bg-[#008b8e] w-full flex justify-center py-[10px] border-2 border-[#b0b6c1]">
						<div className="font-bold text-[20px] py-3">
							{editingFiles ? (newSet ? t("createValidatorSet.header") + ": " + (groupName ?? "") : t("createValidatorSet.editHeader") + ": " + (setName ?? "")) : (setName ?? "")}
						</div>
					</div>

					{/* ContentBox */}
					<div className="flex flex-col bg-[rgba(4, 5, 7, 0.75)] dark:bg-[rgba(55,65,81,0.75)] w-full border-2 border-[#b0b6c1]">

						{/*<Button width="3%" text="⤶" onClick={() => window.history.back()}/>*/}

						<div className="flex flex-row justify-between items-center px-[28px] py-[10px] gap-[10px]">
							{editingFiles ? (<div className="flex flex-row items-center gap-[5px] w-[400px]" />) : (
								<div className="flex flex-row items-center gap-[5px] w-[400px]">
									<label>{t("validator.selectedVersionLabel")}</label>
									<div className="w-[60%]">
										<Select
											options={versions.map(v => { return { label: v.version, value: v.version } })}
											value={versions.length > 0 ? versions.map(v => { return { label: v.version, value: v.version } }).find(ver => ver.label === selectedSetVersion) : { label: "", value: {} }}
											onChange={(option) => setSelectedSetVersion(option.value)}
											className="z-[2]"
										/>
									</div>
								</div>
							)}
							{newSet ?
								(<input className="w-[20%] bg-[#ffffff] dark:bg-gray-700 border border-black border-solid" placeholder={t("createValidatorSet.setNamePlaceholder")} onChange={event => setInputSetName(event.target.value)} />)
								:
								(<label>{(versions[0]?.name ?? "") + '/' + (editingFiles ? tempVersionName : selectedSetVersion) + '/' + selectedSchema.name}</label>)
							}
							{editingFiles ?
								(
									<div className="flex flex-row items-center gap-[5px]">
										{newSet ? ("") : <Button text={t("createValidatorSet.cancelBtn")} onClick={() => window.location.reload()} />}
										<Button text={t("createValidatorSet.confirmBtn")} onClick={() => handleSetVersionCreation()} />
									</div>
								)
								:
								(<Button text={t("createValidatorSet.createSchemaSetBtn")} onClick={() => handleStartSetCreation()} />)
							}
						</div>

						<div className="flex flex-row justify-between items-top px-[28px] py-[10px] gap-[10px]">
							<div className="flex flex-col w-[30%]">
								{newSet || selectedSetVersion === "production" ? ("") :
									editingVersion ? (
										<div className={"flex flex-row items-center w-[100%] " + (editingFiles ? "justify-center gap-[5px]" : "justify-around")}>
											{editingFiles ? (<label>{t("createValidatorSet.newVersionLbl")}:</label>) : ("")}
											<input className="bg-[#ffffff] dark:bg-gray-700 border border-black border-solid" onChange={event => setTempVersionName(event.target.value)} />
											{editingFiles ? ("") : (<Button width="20%" height="40" text={t("createValidatorSet.confirmBtn")} onClick={() => handleVersionEdit()} />)}
											{editingFiles ? ("") : (<Button width="20%" height="40" text={t("createValidatorSet.cancelBtn")} onClick={() => setEditingVersion(false)} />)}
										</div>
									)
										:
										(
											<div className="flex flex-row items-center w-[100%] justify-around">
												<Button width="40%" height="40" text={t("createValidatorSet.editVersionBtn")} onClick={() => setEditingVersion(true)} />
											</div>
										)
								}
								<div className="h-[35px] flex items-center px-[10px] font-bold" />{/*spacer for dependency table*/}
								{/* Schema Table */}
								<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] flex items-center px-[10px] font-bold border border-black">
									<div className="flex-1">{t("createValidatorSet.schemaNameHeader")}</div>
									<div className="flex-1">{t("createValidatorSet.schemaTypeHeader")}</div>
								</div>

								{editingFiles ? (
									tempSchemaSetVer.topSchemas.map((schema, index) => (
										<div className="flex flex-row gap-[5px]" key={index}>
											<Schema
												callback={() => setSelectedSchema(schema)}
												schema={schema}
												index={index}
											/>
											<Button
												text="x"
												width="35"
												height="100%"
												onClick={() => {
													var tempSchemaSetCopy = structuredClone(tempSchemaSetVer);
													tempSchemaSetCopy.topSchemas.splice(index, 1);
													setTempSchemaSetVer(tempSchemaSetCopy);
												}}
											/>
										</div>
									))
								)
									:
									(versions.find(ver => ver.version === selectedSetVersion)?.topSchemas?.map((schema, index) => (
										<Schema
											callback={() => setSelectedSchema(schema)}
											schema={schema}
											index={index}
											key={index}
										/>
									)) ?? (""))}
								{editingFiles ? (
									<div className="flex flex-row items-center gap-[5px] justify-center">
										<label>{t("createValidatorSet.addSchemaLbl")}:</label>
										<input
											type="file"
											accept=".xml,.sch,.xsd,.xslt,xsl"
											onChange={(event) => handleCreateSchemaFromFile(event.target.files[0])}
										/>
									</div>
								) : ("")}

								<div className="h-[35px] flex px-[10px]" />{/*spacer for dependency table*/}
								{(selectedSchema?.dependencies?.length ?? 0) > 0 ? (<label>{t("createDependencyGroupForm.dependenciesHeader")}:</label>) : ("")}
								{(selectedSchema?.dependencies?.length ?? 0) > 0 ? (
									<div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(101,191,255,0.5)] h-[35px] flex items-center px-[10px] font-bold border border-black">
										<div className="flex-1">{t("createValidatorSet.schemaNameHeader")}</div>
										<div className="flex-1">{t("createValidatorSet.schemaTypeHeader")}</div>
									</div>
								)
									:
									("")
								}
							</div>
							{/*input text box*/}
							<div className={"flex flex-col items-center bg-white dark:bg-gray-700 h-[90%] relative gap-[2%] " + (editingFiles ? "w-[65%]" : "w-[65%]")}>
								<div className="overflow-clip rounded-[inherit] size-full">
									<textarea
										id="validatorTextBox"
										className="overflow-scroll h-[1000px] w-full p-[5px] bg-transparent text-black dark:text-white resize-none"
										readOnly
										value={selectedSchema.value === undefined ? "" : selectedSchema.value}
										wrap="off"
									/>
								</div>
								<div className="absolute border border-black inset-0 pointer-events-none" />
							</div>
						</div>
					</div>

				</div>
			</div>
		</div>
	);
};

export default CreateSchemaSetPagina;